import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Megaphone, Target, TrendingUp, Users, Globe2, Check, Sparkles,
  ArrowRight, Zap, Award, BarChart3, MessagesSquare, Send, Building2, BadgePercent, LayoutDashboard,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

// Packs de fallback si l'API rate-card est indisponible
const FALLBACK_PACKS = [
  { key: "starter",    name: "Starter",    model: "CPM", desc: "1 emplacement, ciblage rubrique au choix", price_ar_fmt: "30 000",  features: ["1 bannière sidebar", "1 rubrique ciblée", "Stats temps réel", "Activation sous 24h", "Jusqu'à 10 000 vues/mois"], badge: null,        highlight: false, volume_imp: 10000 },
  { key: "pro",        name: "Pro",        model: "CPM", desc: "Idéal pour les marques en pleine croissance", price_ar_fmt: "75 000",  features: ["3 emplacements (Bannière + Sidebar + Explorer)", "Toutes rubriques (ou ciblage avancé)", "Stats détaillées + export", "Sponsoring 1 sondage offert", "Jusqu'à 50 000 vues/mois", "Support prioritaire"], badge: "POPULAIRE", highlight: true,  volume_imp: 50000 },
  { key: "enterprise", name: "Enterprise", model: "CPM", desc: "Pour campagnes nationales et internationales", price_ar_fmt: "200 000", features: ["Emplacements premium illimités", "Tous les pays (Madagascar + diaspora)", "Sondages illimités", "Notification push aux artistes/fans", "Account manager dédié", "Vues illimitées + tracking conversions"], badge: "VIP",       highlight: false, volume_imp: 200000 },
];

const CATEGORIES_AVAILABLE = [
  { v: "music", l: "🎵 Musique" },
  { v: "humor", l: "😂 Humour" },
  { v: "cinema", l: "🎬 Cinéma" },
  { v: "dance", l: "💃 Danse" },
  { v: "art", l: "🎨 Art" },
  { v: "fashion", l: "👗 Mode" },
  { v: "food", l: "🍲 Cuisine" },
];

export const AdvertisePage = () => {
  const { user } = useAuth();
  const [packs, setPacks] = useState(FALLBACK_PACKS);
  const [meta, setMeta] = useState(null);
  const [advStatus, setAdvStatus] = useState(null);
  const [form, setForm] = useState({
    company: "", contact_name: "", email: "", phone: "",
    budget: "", pack_chosen: "", category_interest: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/rate-card").then(({ data }) => {
      if (data?.packs?.length) {
        setPacks(data.packs);
        setMeta({
          market_ecpm: data.market_ecpm,
          sell_ecpm: data.sell_ecpm,
          margin_pct: data.margin_pct,
          has_real_data: data.has_real_data,
          updated_at: data.updated_at,
        });
      }
    }).catch(() => {});
    if (user) {
      api.get("/me/advertiser").then(({ data }) => setAdvStatus(data?.status || null)).catch(() => {});
    }
  }, [user]);

  const choosePack = (p) => {
    setForm((f) => ({ ...f, pack_chosen: p.key }));
    setTimeout(() => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email) {
      toast.error("Entreprise, nom et email obligatoires");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/advertisers/lead", form);
      setDone(true);
      toast.success("Demande envoyée ! On te recontacte sous 48h ✨");
    } catch {
      toast.error("Échec, réessaie");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="pb-20" data-testid="advertise-page">

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-red via-brand-orange to-brand-yellow px-4 md:px-12 py-20 md:py-28">
        <div className="absolute -top-20 -right-20 text-[400px] opacity-10 select-none">📢</div>
        <div className="relative z-10 max-w-5xl">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold uppercase tracking-widest backdrop-blur mb-4">
            <Sparkles size={11} /> Voamada Ads — La régie publicitaire Malagasy
          </span>
          <h1 className="font-display text-4xl md:text-7xl font-black text-white leading-[0.95] mb-4">
            Touche <span className="underline decoration-white/30">les artistes</span><br />
            et les fans malgaches.
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mb-8">
            Voamada réunit la communauté musicale, culinaire et culturelle de Madagascar.
            Annonce auprès de <strong>milliers de fans engagés</strong> au CPC, CPM ou CPA.
          </p>
          <div className="flex flex-wrap gap-3">
            {advStatus === "approved" && (
              <Link to="/annonceur" className="btn bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/30 inline-flex items-center gap-2" data-testid="advertise-portal-cta">
                <LayoutDashboard size={16} /> Accéder à mon portail annonceur
              </Link>
            )}
            {advStatus === "pending" && (
              <span className="btn bg-brand-yellow/15 text-brand-yellow border border-brand-yellow/40 inline-flex items-center gap-2 cursor-default">
                Demande de compte en cours…
              </span>
            )}
            {!advStatus && user && (
              <Link to="/annonceur" className="btn bg-brand-orange text-white hover:brightness-110 inline-flex items-center gap-2" data-testid="become-advertiser-cta">
                <Building2 size={16} /> Créer mon compte annonceur self-service
              </Link>
            )}
            <a href="#packs" className="btn bg-white text-brand-dark hover:bg-white/90 inline-flex items-center gap-2">
              Voir les tarifs <ArrowRight size={16} />
            </a>
            <a href="#contact-form" className="btn bg-black/30 text-white hover:bg-black/40 backdrop-blur border border-white/30 inline-flex items-center gap-2">
              Demander un devis <Send size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0F0C0B] border-b border-white/5 px-4 md:px-12 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-6xl mx-auto text-center">
          <Stat val="800+" lbl="Artistes inscrits" />
          <Stat val="50K+" lbl="Vidéos diffusées" />
          <Stat val="6" lbl="Pays touchés" />
          <Stat val="98%" lbl="Annonceurs satisfaits" />
        </div>
      </section>

      {/* PACKS */}
      <section id="packs" className="px-4 md:px-12 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest text-brand-orange font-bold mb-2">Tarifs</p>
            <h2 className="font-display text-3xl md:text-5xl font-black mb-3">Choisis ton pack</h2>
            <p className="text-white/60 max-w-2xl mx-auto">Modèles flexibles : CPC (coût par clic), CPM (coût pour 1000 vues) ou CPA (coût par conversion).
            Activation manuelle après validation par notre équipe.</p>
            {meta?.has_real_data && (
              <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-bold uppercase tracking-widest">
                <BadgePercent size={11} /> Tarifs basés sur les performances réelles · eCPM marché {meta.market_ecpm} €
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {packs.map((p) => (
              <div
                key={p.key}
                className={`relative rounded-3xl p-6 md:p-8 border transition ${
                  p.highlight
                    ? "border-brand-orange bg-gradient-to-br from-brand-orange/10 via-brand-yellow/5 to-transparent shadow-2xl scale-[1.02]"
                    : "border-white/10 bg-white/[0.02]"
                }`}
                data-testid={`pack-${p.key}`}
              >
                {p.badge && (
                  <span className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                    p.highlight ? "bg-brand-yellow text-brand-dark" : "bg-brand-red text-white"
                  }`}>
                    {p.badge}
                  </span>
                )}
                <div className="flex items-baseline gap-2 mb-1">
                  <h3 className="font-display text-2xl font-black">{p.name}</h3>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60 font-bold uppercase">{p.model}</span>
                </div>
                <p className="text-xs text-white/55 mb-5">{p.desc}</p>
                <div className="mb-2">
                  <span className="text-4xl font-black">{p.price_ar_fmt}</span>
                  <span className="text-sm text-white/50"> Ar/mois</span>
                </div>
                {p.volume_imp && (
                  <p className="text-[11px] text-white/45 mb-4">≈ {p.volume_imp.toLocaleString("fr-FR")} impressions garanties · {p.cpm_effective_eur ? `${p.cpm_effective_eur} € eCPM` : ""}</p>
                )}
                <ul className="space-y-2 mb-6">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                      <Check size={14} className="text-emerald-400 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => choosePack(p)}
                  className={`w-full py-3 rounded-xl font-bold text-sm transition ${
                    p.highlight
                      ? "bg-gradient-to-r from-brand-red to-brand-orange text-white hover:brightness-110"
                      : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                  }`}
                  data-testid={`pack-cta-${p.key}`}
                >
                  {p.key === "enterprise" ? "Nous contacter" : `Choisir ${p.name}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TARGETING */}
      <section className="bg-[#15100D] px-4 md:px-12 py-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-orange font-bold mb-2">Ciblage avancé</p>
            <h2 className="font-display text-3xl md:text-5xl font-black mb-4">7 rubriques disponibles</h2>
            <p className="text-white/60 mb-6">Diffuse ta pub uniquement auprès de l'audience qui t'intéresse.
            Les utilisateurs Voamada sont segmentés par catégorie d'intérêt.</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES_AVAILABLE.map((c) => (
                <span key={c.v} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm">{c.l}</span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Feature icon={<Target size={20} />} title="Ciblage rubrique" desc="Touche les fans de cuisine, mode, musique..." />
            <Feature icon={<Globe2 size={20} />} title="Pays au choix" desc="Madagascar, France, Réunion, diaspora..." />
            <Feature icon={<BarChart3 size={20} />} title="Stats temps réel" desc="Vues, clics, CTR, conversions" />
            <Feature icon={<MessagesSquare size={20} />} title="Sondages" desc="Questionne directement la communauté" />
          </div>
        </div>
      </section>

      {/* WHY VOAMADA */}
      <section className="px-4 md:px-12 py-16">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs uppercase tracking-widest text-brand-orange font-bold mb-2">Pourquoi Voamada ?</p>
          <h2 className="font-display text-3xl md:text-5xl font-black mb-10">3 raisons de nous choisir</h2>
        </div>
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          <Reason icon={<Users size={28} />} title="Audience qualifiée" desc="Une communauté malagasy active et engagée, pas de bots." />
          <Reason icon={<Zap size={28} />} title="Activation rapide" desc="Ta campagne est en ligne en 24h après validation." />
          <Reason icon={<Award size={28} />} title="100% local" desc="On parle ton marché, on connaît tes clients." />
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="contact-form" className="bg-gradient-to-br from-brand-red/10 to-brand-orange/5 px-4 md:px-12 py-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-widest text-brand-orange font-bold mb-2">Demande de devis</p>
            <h2 className="font-display text-3xl md:text-5xl font-black mb-3">Prêt à lancer ta campagne ?</h2>
            <p className="text-white/60">On te recontacte sous 48h avec une proposition adaptée.</p>
          </div>

          {done ? (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-8 text-center" data-testid="lead-success">
              <div className="text-6xl mb-3">🎉</div>
              <h3 className="font-display text-2xl font-black mb-2">Demande envoyée !</h3>
              <p className="text-white/70 mb-4">Notre équipe te contactera sous 48h à <strong>{form.email}</strong>.</p>
              <Link to="/" className="btn-primary">Retour à l'accueil</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-black/40 p-6 md:p-8 space-y-4" data-testid="lead-form">
              <div className="grid md:grid-cols-2 gap-4">
                <Input label="Entreprise *" value={form.company} onChange={(v) => setForm({ ...form, company: v })} placeholder="Ma société Sàrl" testid="lead-company" required />
                <Input label="Contact *" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} placeholder="Tia Razafy" testid="lead-name" required />
                <Input label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="contact@masociete.mg" testid="lead-email" required />
                <Input label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+261 34 XX XXX XX" testid="lead-phone" />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <Select label="Pack souhaité" value={form.pack_chosen} onChange={(v) => setForm({ ...form, pack_chosen: v })} testid="lead-pack"
                  options={[{ v: "", l: "Choisir…" }, ...packs.map((p) => ({ v: p.key, l: p.name }))]} />
                <Select label="Rubrique cible" value={form.category_interest} onChange={(v) => setForm({ ...form, category_interest: v })} testid="lead-category"
                  options={[{ v: "", l: "Toutes" }, ...CATEGORIES_AVAILABLE.map((c) => ({ v: c.v, l: c.l }))]} />
                <Select label="Budget mensuel" value={form.budget} onChange={(v) => setForm({ ...form, budget: v })} testid="lead-budget"
                  options={[
                    { v: "", l: "À définir…" },
                    { v: "0-50k", l: "< 50 000 Ar" },
                    { v: "50-100k", l: "50 000 - 100 000 Ar" },
                    { v: "100-300k", l: "100 000 - 300 000 Ar" },
                    { v: "300k+", l: "> 300 000 Ar" },
                  ]} />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 font-bold block mb-1.5">Message (objectifs, produit, infos utiles)</label>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4}
                  placeholder="Décris ton projet en quelques mots…"
                  className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-brand-orange/60 resize-y"
                  data-testid="lead-message" />
              </div>

              <button type="submit" disabled={submitting} className="w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="lead-submit">
                {submitting ? "Envoi…" : <>Envoyer ma demande <Send size={14} /></>}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

const Stat = ({ val, lbl }) => (
  <div>
    <div className="text-3xl md:text-5xl font-display font-black text-brand-yellow">{val}</div>
    <p className="text-xs md:text-sm text-white/50 uppercase tracking-widest font-bold mt-1">{lbl}</p>
  </div>
);

const Feature = ({ icon, title, desc }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
    <div className="text-brand-orange mb-2">{icon}</div>
    <h4 className="font-bold text-sm mb-1">{title}</h4>
    <p className="text-xs text-white/55">{desc}</p>
  </div>
);

const Reason = ({ icon, title, desc }) => (
  <div className="text-center rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-brand-orange/30 transition">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-red/20 to-brand-orange/20 text-brand-orange mb-3">
      {icon}
    </div>
    <h3 className="font-display text-lg font-black mb-1">{title}</h3>
    <p className="text-sm text-white/60">{desc}</p>
  </div>
);

const Input = ({ label, value, onChange, placeholder, type = "text", testid, required }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-white/50 font-bold block mb-1.5">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required}
      className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-brand-orange/60"
      data-testid={testid} />
  </div>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-white/50 font-bold block mb-1.5">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-brand-orange/60"
      data-testid={testid}>
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);

export default AdvertisePage;
